import { useState } from 'react';
import { Download, ChevronDown, ChevronUp, X, CheckCircle, XCircle } from 'lucide-react';
import { format } from "date-fns";
import ticketApi from '../ticket-dashboard/services/ticketApi';
import Papa from 'papaparse';
import useQuery from '@helpers/JIFI/useQuery';



import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';

import { RadioGroup, RadioGroupItem } from '@shadcn/components/ui/radio-group';
import { Label } from '@shadcn/components/ui/label';
import { Input } from '@shadcn/components/ui/input';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shadcn/components/ui/collapsible';
import { Badge } from '@shadcn/components/ui/badge';
import { Separator } from '@shadcn/components/ui/separator';
import { getLabel } from '@pages/JIFI/warranty-claim';
import { FAULT_REASONS } from '@pages/JIFI/warranty-claim/warranty-claim';

// Helper function to join monogram values
export const joinMonogramValues = (monogramArray, field) => {
  if (!monogramArray || !Array.isArray(monogramArray)) return '';
  return monogramArray
    .map(item => item?.[field] || '')
    .filter(value => value !== '')
    .join(', ');
};

const TicketExportPage = () => {
  const [selectedSystem, setSelectedSystem] = useState('WARRANTY'); // Single select
  const [selectedStoreNames, setSelectedStoreNames] = useState([]); // Multiple select
  const [dateRange, setDateRange] = useState({
    from: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd')
  });
  const [dateErrors, setDateErrors] = useState({
    from: '',
    to: ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null); // 'success', 'error', or null
  const [showStores, setShowStores] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [summaryDismissed, setSummaryDismissed] = useState(false);

  const systemOptions = [
    { value: 'WARRANTY', label: 'Warranty' },
    { value: 'ORDER', label: 'Order' },
    { value: 'REPAIR', label: 'Repair' }
  ];


  const [retailStores, stateRetailStore] = useQuery({
    url: `/inventory/getRetailStore`,
  });

  const storeOptions = [
    { value: '', label: 'All Stores' },
    ...(retailStores?.map((store) => ({
      value: store.name,
      label: store.name,
    })) || [])
  ];

  const handleSystemChange = (system) => {
    setSelectedSystem(system);
  };

  const handleStoreNameChange = (storeName) => {
    if (storeName === '') {
      
      setSelectedStoreNames([]);
    } else {
      
      setSelectedStoreNames(prev =>
        prev.includes(storeName)
          ? prev.filter(name => name !== storeName)
          : [...prev, storeName]
      );
    }
  };

  const validateDateRange = (fromDate, toDate) => {
    const errors = { from: '', to: '' };

    if (!fromDate) {
      errors.from = 'From date is required';
    }

    if (!toDate) {
      errors.to = 'To date is required';
    }

    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      errors.to = 'To date must be after from date';
    }

    return errors;
  };

  const handleDateChange = (field, value) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);

    // Validate dates
    const errors = validateDateRange(newDateRange.from, newDateRange.to);
    setDateErrors(errors);
  };

  const flattenTicketData = (tickets) => {
    const flattenedData = [];

    tickets.forEach(ticket => {
      const baseData = {
        'Ticket Type': ticket.ticketType,
        'Customer Name': ticket.customerName,
        'Batch Code': ticket.batchCode,
        'Staff Name': ticket.staffName,
        'Order Number': ticket.orderNumber,
        'Store Name': ticket.storeName,
        'Closed Date': ticket.closedAt,
        'Status': ticket.status?.originalStatus,
      };

      if (ticket.ticketType === 'ORDER') {
        flattenedData.push({
          ...baseData,
          'Action Description': ticket.details?.actionDescription,
          'Day Needed By': ticket.details?.dayNeededBy,
          'To Be Shipped': ticket.details?.toBeShipped ? 'Yes' : 'No',
          'Department': ticket.details?.department,
          'Attachments': ticket.details?.attachments?.join('; ') || ''
        });
      } else if (ticket.ticketType === 'WARRANTY') {
        // Handle warranty with replacement SKUs (1-to-many relationship)
        if (ticket.details?.replacementSkus && ticket.details.replacementSkus.length > 0) {
          ticket.details.replacementSkus.forEach((replacementSku, index) => {
            flattenedData.push({
              ...baseData,
              'Fault Reason': getLabel(FAULT_REASONS, ticket.details?.faultReason),
              'Customer Request': ticket.details?.customerRequest,
              'Is Fulfilled': ticket.details?.isFulfilled ? 'Yes' : 'No',
              'Attachments': ticket.details?.attachments?.join('; ') || '',
              // Replacement SKU details
              'Replacement Item #': index + 1,
              'Original SKU': replacementSku.originalSku,
              'Replacement SKU': replacementSku.replacementSku,
              'Monogram Text': joinMonogramValues(replacementSku.monogram, 'text'),
              'Monogram Font': joinMonogramValues(replacementSku.monogram, 'font'),
              'Monogram Color': joinMonogramValues(replacementSku.monogram, 'color'),
              'Monogram Location': joinMonogramValues(replacementSku.monogram, 'location'),
              'Pickup Location': retailStores?.find(store => store._id === replacementSku.pickupLocation)?.name || '',
              'Replacement To Be Shipped': replacementSku.toBeShipped ? 'Yes' : 'No',
              'Replacement Date Needed By': replacementSku.dateNeededBy
            });
          });
        } else {
          flattenedData.push({
            ...baseData,
            'Fault Reason': getLabel(FAULT_REASONS, ticket.details?.faultReason),
            'Customer Request': ticket.details?.customerRequest,
            'Is Fulfilled': ticket.details?.isFulfilled ? 'Yes' : 'No',
            'Attachments': ticket.details?.attachments?.join('; ') || ''
          });
        }
      } else if (ticket.ticketType === 'REPAIR') {

        if (ticket.details?.itemRepairs && ticket.details.itemRepairs.length > 0) {
          ticket.details.itemRepairs.forEach((itemRepair) => {
            if (itemRepair.parts && itemRepair.parts.length > 0) {

              itemRepair.parts.forEach((part, partIndex) => {
                flattenedData.push({
                  ...baseData,
                  'Is Completed': ticket.details?.isCompleted ? 'Yes' : 'No',
                  'Additional Notes': ticket.details?.additionalNotes,
                  'Attachments': ticket.details?.attachments?.join('; ') || '',
                  // Item repair details
                  'Repair Item SKU': itemRepair.sku,
                  'Repair Types': itemRepair.repairTypes?.join('; ') || '',
                  'Repair To Be Shipped': itemRepair.toBeShipped ? 'Yes' : 'No',
                  'Repair Pickup Location': retailStores?.find(store => store._id === itemRepair.pickupLocation)?.name || '',
                  'Repair Date Needed By': itemRepair.dateNeededBy,
                  // Parts details
                  'Part #': partIndex + 1,
                  'Part Name': part.name,
                  'Part Quantity': part.quantity
                });
              });
            } else {
              flattenedData.push({
                ...baseData,
                'Is Completed': ticket.details?.isCompleted ? 'Yes' : 'No',
                'Additional Notes': ticket.details?.additionalNotes,
                'Attachments': ticket.details?.attachments?.join('; ') || '',
                // Item repair details without parts
                'Repair Item SKU': itemRepair.sku,
                'Repair Types': itemRepair.repairTypes?.join('; ') || '',
                'Repair To Be Shipped': itemRepair.toBeShipped ? 'Yes' : 'No',
                'Repair Pickup Location': retailStores?.find(store => store._id === itemRepair.pickupLocation)?.name || '',
                'Repair Date Needed By': itemRepair.dateNeededBy
              });
            }
          });
        } else {
          flattenedData.push({
            ...baseData,
            'Is Completed': ticket.details?.isCompleted ? 'Yes' : 'No',
            'Additional Notes': ticket.details?.additionalNotes,
            'Attachments': ticket.details?.attachments?.join('; ') || ''
          });
        }
      } else {
        // Fallback for unknown ticket types
        flattenedData.push(baseData);
      }
    });

    return flattenedData;
  };

  const handleExport = async () => {
    if (!selectedSystem) {
      setExportStatus('error');
      setTimeout(() => setExportStatus(null), 3000);
      return;
    }

    // Validate date range before export
    const errors = validateDateRange(dateRange.from, dateRange.to);
    if (errors.from || errors.to) {
      setDateErrors(errors);
      setExportStatus('error');
      setTimeout(() => setExportStatus(null), 3000);
      return;
    }

    try {
      setIsExporting(true);
      setExportStatus(null);

      const params = {
        type: selectedSystem,
        dateFrom: dateRange.from,
        dateTo: dateRange.to
      };

      // Add store names if selected (comma-separated)
      if (selectedStoreNames.length > 0) {
        params.storeName = selectedStoreNames.join(',');
      }

      const tickets = await ticketApi.exportTickets(params);

      if (!tickets || tickets.length === 0) {
        setExportStatus('error');
        setTimeout(() => setExportStatus(null), 3000);
        return;
      }

      console.log("current tickets", tickets);
      const flattenedData = flattenTicketData(tickets);

      console.log("flattenedData", flattenedData);
      // Create CSV
      const csv = Papa.unparse(flattenedData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `ticket-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Set success status
      setExportStatus('success');
      setTimeout(() => setExportStatus(null), 3000);

    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus('error');
      setTimeout(() => setExportStatus(null), 3000);
    } finally {
      setIsExporting(false);
    }
  };



  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black mb-2">Ticket Export</h1>
          <p className="text-gray-600">Export ticket data to CSV format with customizable filters</p>
        </div>

        {/* Export Summary Card */}
        {selectedSystem && !summaryDismissed && (
          <Card className="mb-6 border-gray-200">
            <Collapsible open={showSummary} onOpenChange={setShowSummary}>
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-0 h-auto self-start">
                        <div className="flex items-center space-x-2">
                          {showSummary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <span className="text-sm font-medium">Export Summary</span>
                        </div>
                      </Button>
                    </CollapsibleTrigger>
                    
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge variant="outline" className="text-black border-black">
                        {systemOptions.find(opt => opt.value === selectedSystem)?.label}
                      </Badge>
                      <span className="text-gray-400 hidden sm:inline">•</span>
                      <Badge variant="secondary">
                        {selectedStoreNames.length === 0 ? 'All Stores' : `${selectedStoreNames.length} stores`}
                      </Badge>
                     
                     
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSummaryDismissed(true)}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 self-start sm:self-center"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <Separator className="mb-4" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 font-medium">System:</span>
                      <p className="text-black font-semibold">{systemOptions.find(opt => opt.value === selectedSystem)?.label}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Stores:</span>
                      <p className="text-black">
                        {selectedStoreNames.length === 0
                          ? 'All Stores'
                          : selectedStoreNames.length === 1
                            ? selectedStoreNames[0]
                            : `${selectedStoreNames.length} stores selected`
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Period:</span>
                      <p className="text-black">{format(new Date(dateRange.from), 'MMM dd, yyyy')} - {format(new Date(dateRange.to), 'MMM dd, yyyy')}</p>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column: System and Store Selection */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* System Selection Card */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">System Type</CardTitle>
                <CardDescription>Select the ticket system to export data from</CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup value={selectedSystem} onValueChange={handleSystemChange}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {systemOptions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-3">
                        <RadioGroupItem value={option.value} id={option.value} />
                        <Label  htmlFor={option.value} className="font-medium mb-0 cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Store Selection Card */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Store Selection</CardTitle>
                <CardDescription>Choose specific stores or select all stores</CardDescription>
              </CardHeader>
              <CardContent>
                <Collapsible open={showStores} onOpenChange={setShowStores}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between" disabled={stateRetailStore.isLoading}>
                      <span>
                        {stateRetailStore.isLoading ? 'Loading...' :
                          selectedStoreNames.length === 0 ? 'All Stores' :
                            `${selectedStoreNames.length} stores selected`}
                      </span>
                      {showStores ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <Card className="mt-4 max-h-64 overflow-y-auto">
                      <CardContent className="p-0">
                        {storeOptions.map((option) => (
                          <div key={option.value} className="flex items-center space-x-3 p-3 border-b border-gray-100 last:border-b-0">
                            <Checkbox
                              id={`store-${option.value}`}
                              checked={option.value === '' ? selectedStoreNames.length === 0 : selectedStoreNames.includes(option.value)}
                              onCheckedChange={() => handleStoreNameChange(option.value)}
                            />
                            <Label htmlFor={`store-${option.value}`} className="cursor-pointer flex-1 mb-0">
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>

            {/* Date Range Card */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Date Range</CardTitle>
                <CardDescription>Select the date range for ticket data export</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="from-date">From Date</Label>
                    <div className="relative">
                      <Input
                        id="from-date"
                        type="date"
                        value={dateRange.from}
                        onChange={(e) => handleDateChange('from', e.target.value)}
                        className={`${dateErrors.from ? 'border-red-500' : ''}`}
                      />
                      
                    </div>
                    {dateErrors.from && (
                      <p className="text-xs text-red-500">{dateErrors.from}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="to-date">To Date</Label>
                    <div className="relative">
                      <Input
                        id="to-date"
                        type="date"
                        value={dateRange.to}
                        onChange={(e) => handleDateChange('to', e.target.value)}
                        className={`${dateErrors.to ? 'border-red-500' : ''}`}
                      />
                      
                    </div>
                    {dateErrors.to && (
                      <p className="text-xs text-red-500">{dateErrors.to}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Export Controls */}
          <div className="space-y-6">
            
            {/* Export Card */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Export Data</CardTitle>
                <CardDescription>Download your filtered ticket data as CSV</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleExport}
                  disabled={isExporting || !selectedSystem || dateErrors.from || dateErrors.to}
                  className="w-full bg-black hover:bg-gray-800 text-white font-medium py-6 text-base"
                  size="lg"
                >
                  {isExporting ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Exporting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Download className="h-5 w-5" />
                      <span>Export CSV</span>
                    </div>
                  )}
                </Button>

                {/* Export Status */}
                {isExporting && (
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Processing your request...</p>
                  </div>
                )}

                {exportStatus === 'success' && (
                  <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <p className="text-sm text-green-700 font-medium">Export completed successfully!</p>
                  </div>
                )}

                {exportStatus === 'error' && (
                  <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <p className="text-sm text-red-700">Export failed. Please try again.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats Card */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Selected System</span>
                  <Badge variant={selectedSystem ? "default" : "secondary"} className="bg-black text-white">
                    {selectedSystem ? systemOptions.find(opt => opt.value === selectedSystem)?.label : 'None'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Store Filter</span>
                  <Badge variant="outline">
                    {selectedStoreNames.length === 0 ? 'All' : selectedStoreNames.length}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Date Range</span>
                  <span className="text-sm text-black font-medium">
                    {Math.ceil((new Date(dateRange.to) - new Date(dateRange.from)) / (1000 * 60 * 60 * 24))} days
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketExportPage;
